
import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector, shallowEqual } from "react-redux";

import {
    getMessagesByChat,
    sendTextMessage,
    sendMediaMessage,
    sendVoiceMessage,
    sendVideoNoteMessage,
    retryFailedMessage,
    discardFailedMessage,
    canRetryMessage,
    recordingExtension,
    shareEntityToChat,
    shareProductToChat,
    shareOrderToChat,
    markMessagesAsRead,
    deleteMessage,
    receiveMessage,
    receiveMessageUpdate,
    editMessageText,
    deleteMessageSocket,
    markMessagesAsReadSocket,
} from "../../actions/message";

import { setActiveChatIdLocal } from "../../actions/chatActions";
import { socket, joinSocketUser } from "../../socket/socket";
import { playDing } from "../../utils/notifyAudio";
import { acquireChatRoom, releaseChatRoom } from "../../utils/chatRoomMembership";
import {
    clearChatViewportState,
    isChatMessageActuallySeen,
    isPageEngaged,
    setChatViewportState,
} from "../../utils/chatVisibility";

import HeaderChatUI from "./HeaderChatUI";
import ChatMessages from "./ChatMessages";
import InputChatUI from "./InputChatUI";
import VideoNoteRecorder from "./VideoNoteRecorder";
import { DeleteMessagesDialog, ScheduleDialog, ScheduledMessagesDialog } from "./ChatDialogs";
// Что у проектов разное (задачи, каталог сайта, уведомления, плавающее окно) —
// в chatPlatform.js: у админки и у фронта он свой, остальные файлы чата общие.
import {
    CHAT_SITE_CATALOG,
    ChatUserProfileModal,
    TodoFromMessageModal,
    openChatFloating,
    useChatReadExtras,
} from "./chatPlatform";
import {
    MAX_ATTACHMENTS,
    albumClientId,
    createAlbumId,
    isAlbumPhoto,
} from "../../utils/chatAlbums";
import {
    createScheduledMessage,
    fetchScheduledMessages,
    scheduleText,
    scheduledErrorText,
} from "../../utils/scheduledMessagesApi";
import {
    downloadBlob,
    fetchChatAttachment,
    getChatAttachmentName,
    hasSecureChatAttachment,
} from "../../utils/chatAttachmentStore";
import { getChatDraft, setChatDraft } from "../../utils/chatDrafts";

const getId = (value) => String(value?._id || value || "");

const CHAT_BACKGROUND_STORAGE_PREFIX = "chat-background:v1:";
const DEFAULT_CHAT_BACKGROUND = { preset: "default", image: null };

const readChatBackground = (chatId) => {
    if (!chatId || typeof window === "undefined") return DEFAULT_CHAT_BACKGROUND;

    try {
        const raw = window.localStorage.getItem(`${CHAT_BACKGROUND_STORAGE_PREFIX}${chatId}`);
        if (!raw) return DEFAULT_CHAT_BACKGROUND;

        const parsed = JSON.parse(raw);
        const preset = ["default", "soft", "dots", "grid", "custom"].includes(parsed?.preset)
            ? parsed.preset
            : "default";

        return {
            preset,
            image: preset === "custom" && typeof parsed?.image === "string"
                ? parsed.image
                : null,
        };
    } catch (_error) {
        return DEFAULT_CHAT_BACKGROUND;
    }
};


/** Один файл — не больше 50 МБ (как в приложении). */
const MAX_FILE_BYTES = 50 * 1024 * 1024;
/** В задачу за один раз — не больше 20 сообщений. */
const MAX_TODO_MESSAGES = 20;

/** Текст сообщения для копирования: без служебной строки старого ответа, у голосового — расшифровка. */
const getCopyText = (message) => {
    let text = String(message?.text || "");
    if (text.startsWith("↩️ ")) {
        const lineBreak = text.indexOf("\n");
        text = lineBreak >= 0 ? text.slice(lineBreak + 1) : "";
    }
    text = text.trim();
    if (!text && message?.voiceId && typeof message.voiceId === "object") {
        text = String(message.voiceId.transcriptText || "").trim();
    }
    return text;
};

const copyToClipboard = async (text) => {
    try {
        if (navigator?.clipboard?.writeText) {
            await navigator.clipboard.writeText(text);
            return true;
        }
    } catch (_error) {
        // ниже — запасной способ
    }
    const area = document.createElement("textarea");
    area.value = text;
    area.style.position = "fixed";
    area.style.opacity = "0";
    document.body.appendChild(area);
    area.select();
    try {
        return document.execCommand("copy");
    } finally {
        area.remove();
    }
};

const getReplyPayload = (message) => {
    if (!message) return null;

    const sender = message.sender || {};
    const author = message.guestSender?.name ||
        [sender.firstName, sender.lastName].filter(Boolean).join(" ").trim() ||
        sender.email ||
        "Собеседник";
    const snippet = (message.text || message.entity?.title || message.fileName || "Вложение")
        .trim()
        .slice(0, 180);

    return { messageId: message._id, author, snippet };
};

export default function ChatCore({
    chat,
    officeId,
    office,
    role,
    chats = [],
    focusMessageId = null,
    onForwardRequest = null,
    onOpenForwardOrigin = null,
    onBack = null,
    realtimeManaged = false,
    mode = "default",
    onTypingUsersChange = null,
    /** userId → { online, lastSeenAt }: кто в сети (если страница это знает). */
    presenceFor = null,
}) {
    const dispatch = useDispatch();
    const chatId = chat?._id;

    const { user } = useSelector((state) => state.auth || {}, shallowEqual);
    const {
        messages = [],
        loading = false,
        sending = false,
        sendError = null,
        nextCursorByChat = {},
        hasMoreByChat = {},
        loadingOlderByChat = {},
    } = useSelector((state) => state.message || {}, shallowEqual);


    const userId = user?._id || user?.id;
    // Прочитали чат — что ещё отметить (на фронте: уведомления этого чата).
    const markReadExtras = useChatReadExtras(chatId);
    const nextCursor = nextCursorByChat?.[chatId] || null;
    const hasMore = !!hasMoreByChat?.[chatId];
    const loadingOlder = !!loadingOlderByChat?.[chatId];

    // Текст поля. Начинаем с черновика этого чата (utils/chatDrafts.js).
    const [text, setTextState] = useState(() => getChatDraft(userId, chatId));
    const draftOwnerRef = useRef({ userId, chatId });
    draftOwnerRef.current = { userId, chatId };
    /** Поменять текст поля и сразу сохранить его как черновик этого чата. */
    const setText = useCallback((value) => {
        setTextState(value);
        const owner = draftOwnerRef.current;
        if (owner.chatId) setChatDraft(owner.userId, owner.chatId, value);
    }, []);
    /** Чей профиль открыт (пользователь или его id). */
    const [profileUser, setProfileUser] = useState(null);
    const openProfile = useCallback((person) => {
        if (getId(person)) setProfileUser(person);
    }, []);
    const closeProfile = useCallback(() => setProfileUser(null), []);
    /** Выбранные вложения (до 10): фото уйдут альбомом. */
    const [files, setFiles] = useState([]);
    const [attachError, setAttachError] = useState("");
    /** Короткая надпись внизу чата («Отправится завтра в 09:00»). */
    const [notice, setNotice] = useState("");
    const [scheduled, setScheduled] = useState([]);
    const [scheduledOpen, setScheduledOpen] = useState(false);
    /** Сколько отложенных сейчас загружается на сервер. */
    const [scheduling, setScheduling] = useState(0);
    const [videoNoteOpen, setVideoNoteOpen] = useState(false);
    /** Записанный кружок, которому выбирают время отправки. */
    const [noteToSchedule, setNoteToSchedule] = useState(null);
    const [deleteTargets, setDeleteTargets] = useState(null);
    const [todoMessages, setTodoMessages] = useState(null);
    /** Сообщение, к которому перешли по цитате: подсвечено пару секунд. */
    const [highlightId, setHighlightId] = useState("");
    const [dragOver, setDragOver] = useState(false);
    const [replyTo, setReplyTo] = useState(null);
    const [editingMessage, setEditingMessage] = useState(null);
    const [editText, setEditText] = useState("");
    const [editBusy, setEditBusy] = useState(false);
    const [editError, setEditError] = useState("");
    const [selectedId, setSelectedId] = useState(null);
    const [typingUsers, setTypingUsers] = useState([]);
    const [showJumpToBottom, setShowJumpToBottom] = useState(false);
    const [newMessagesCount, setNewMessagesCount] = useState(0);
    const [selectedMessageIds, setSelectedMessageIds] = useState([]);
    const [selectionLimitHit, setSelectionLimitHit] = useState(false);
    const [chatBackground, setChatBackground] = useState(() => readChatBackground(chatId));

    const scrollRef = useRef(null);
    const inputRef = useRef(null);
    const noticeTimerRef = useRef(null);
    const highlightTimerRef = useRef(null);
    const dragDepthRef = useRef(0);
    const atBottomRef = useRef(true);
    const shouldScrollRef = useRef(false);
    const typingTimerRef = useRef(null);
    const markReadTimerRef = useRef(null);
    const loadingOlderRef = useRef(false);
    const realtimeManagedRef = useRef(realtimeManaged);
    const roomOwnerRef = useRef(
        `chat-core-room-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`
    );
    const chatViewportInstanceRef = useRef(
        `chat-view-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`
    );

    useEffect(() => {
        realtimeManagedRef.current = realtimeManaged;
    }, [realtimeManaged]);

    useEffect(() => {
        setChatBackground(readChatBackground(chatId));
    }, [chatId]);

    const handleChatBackgroundChange = useCallback((nextBackground) => {
        if (!chatId) return false;

        const preset = ["default", "soft", "dots", "grid", "custom"].includes(nextBackground?.preset)
            ? nextBackground.preset
            : "default";
        const normalized = {
            preset,
            image: preset === "custom" && typeof nextBackground?.image === "string"
                ? nextBackground.image
                : null,
        };

        try {
            if (typeof window !== "undefined") {
                window.localStorage.setItem(
                    `${CHAT_BACKGROUND_STORAGE_PREFIX}${chatId}`,
                    JSON.stringify(normalized)
                );
            }
            setChatBackground(normalized);
            return true;
        } catch (_error) {
            return false;
        }
    }, [chatId]);

    useEffect(() => {
        onTypingUsersChange?.(typingUsers);
    }, [typingUsers, onTypingUsersChange]);

    useEffect(() => {
        return () => {
            onTypingUsersChange?.([]);
        };
    }, [onTypingUsersChange]);

    const chatMessages = useMemo(() => (
        (messages || []).filter((message) => getId(message.chatId) === getId(chatId))
    ), [messages, chatId]);

    const selectedMessages = useMemo(() => {
        const selectedSet = new Set(selectedMessageIds.map(getId));
        return chatMessages.filter((message) => selectedSet.has(getId(message?._id)));
    }, [chatMessages, selectedMessageIds]);


    const syncViewportState = useCallback((atBottom = atBottomRef.current) => {
        if (!chatId) return;

        setChatViewportState({
            instanceId: chatViewportInstanceRef.current,
            chatId,
            atBottom,
        });
    }, [chatId]);

    const scrollToBottom = useCallback((behavior = "auto") => {
        const el = scrollRef.current;
        if (!el) return;

        if (behavior === "smooth" && typeof el.scrollTo === "function") {
            el.scrollTo({ top: el.scrollHeight, behavior: "smooth" });
        } else {
            el.scrollTop = el.scrollHeight;
        }

        atBottomRef.current = true;
        syncViewportState(true);
        setShowJumpToBottom(false);
        setNewMessagesCount(0);
    }, [syncViewportState]);

    const scrollToMessage = useCallback((messageId) => {
        if (!messageId) return false;
        // Фото из альбома своей строки не имеет — ищем его плитку.
        const el = document.getElementById(`chat-message-${messageId}`) ||
            document.querySelector(`[data-chat-message-id="${String(messageId).replace(/"/g, "")}"]`)?.closest?.(".chat-message-AO, .chat-message-row-AO");
        if (!el) return false;

        el.scrollIntoView({ block: "center", behavior: "smooth" });
        return true;
    }, []);

    const requestScroll = useCallback(() => {
        shouldScrollRef.current = true;
    }, []);

    const scheduleMarkRead = useCallback(() => {
        if (!chatId || !userId) return;

        // Read receipt отправляем только когда пользователь реально видит чат:
        // вкладка активна, окно в фокусе и список находится у нижней границы.
        if (!isChatMessageActuallySeen(chatId)) return;

        if (markReadTimerRef.current) clearTimeout(markReadTimerRef.current);

        markReadTimerRef.current = setTimeout(() => {
            // Повторная проверка важна: за 450 мс пользователь мог переключить
            // вкладку/окно или уйти вверх по истории.
            if (!isChatMessageActuallySeen(chatId)) {
                markReadTimerRef.current = null;
                return;
            }

            dispatch(markMessagesAsRead(chatId, userId));
            markReadExtras?.();


            markReadTimerRef.current = null;
        }, 450);
    }, [dispatch, chatId, userId, markReadExtras]);

    const loadOlderMessages = useCallback(async () => {
        const el = scrollRef.current;

        if (!el || !chatId || !nextCursor || !hasMore || loadingOlder || loadingOlderRef.current) return;

        loadingOlderRef.current = true;
        const prevHeight = el.scrollHeight;
        const prevTop = el.scrollTop;

        await dispatch(getMessagesByChat(chatId, {
            before: nextCursor,
            limit: 30,
            append: true,
        }));

        requestAnimationFrame(() => {
            const target = scrollRef.current;
            if (target) target.scrollTop = target.scrollHeight - prevHeight + prevTop;
            loadingOlderRef.current = false;
        });
    }, [dispatch, chatId, nextCursor, hasMore, loadingOlder]);

    useLayoutEffect(() => {
        if (focusMessageId && scrollToMessage(focusMessageId)) {
            shouldScrollRef.current = false;
            return;
        }

        if (!shouldScrollRef.current) return;
        shouldScrollRef.current = false;
        scrollToBottom();
    }, [chatMessages.length, focusMessageId, scrollToBottom, scrollToMessage]);

    useEffect(() => {
        if (!chatId) return undefined;

        // Открыли другой чат — в поле его черновик (или пусто).
        setTextState(getChatDraft(userId, chatId));
        setFiles([]);
        setAttachError("");
        setScheduled([]);
        setScheduledOpen(false);
        setDeleteTargets(null);
        setTodoMessages(null);
        setReplyTo(null);
        setSelectedId(null);
        setTypingUsers([]);
        setShowJumpToBottom(false);
        setNewMessagesCount(0);
        setSelectedMessageIds([]);
        setSelectionLimitHit(false);
        atBottomRef.current = true;
        syncViewportState(true);

        if (mode !== "floating") {
            dispatch(setActiveChatIdLocal(chatId));
        }

        let cancelled = false;

        const loadInitialMessages = async () => {
            if (!focusMessageId) requestScroll();

            const firstPage = await dispatch(getMessagesByChat(chatId, {
                limit: focusMessageId ? 100 : 50,
                append: false,
            }));

            if (cancelled || !focusMessageId) return;

            const focusId = getId(focusMessageId);
            let found = (firstPage?.messages || []).some(
                (message) => getId(message?._id) === focusId
            );
            let cursor = firstPage?.nextCursor || null;
            let page = 0;

            while (!found && cursor && page < 30 && !cancelled) {
                const older = await dispatch(getMessagesByChat(chatId, {
                    before: cursor,
                    limit: 100,
                    append: true,
                }));

                found = (older?.messages || []).some(
                    (message) => getId(message?._id) === focusId
                );
                cursor = older?.nextCursor || null;
                page += 1;
            }

            if (found && !cancelled) {
                window.requestAnimationFrame(() => {
                    scrollToMessage(focusMessageId);
                });
            }
        };

        loadInitialMessages();

        return () => {
            cancelled = true;
            if (markReadTimerRef.current) clearTimeout(markReadTimerRef.current);
            if (typingTimerRef.current) clearTimeout(typingTimerRef.current);
            markReadTimerRef.current = null;
            typingTimerRef.current = null;
            clearChatViewportState(chatViewportInstanceRef.current);
            if (mode !== "floating") {
                dispatch(setActiveChatIdLocal(null));
            }
        };
    }, [
        dispatch,
        chatId,
        focusMessageId,
        requestScroll,
        scrollToMessage,
        syncViewportState,
        mode,
    ]);

    useEffect(() => {
        if (!chatId || !userId) return;

        const hasUnread = chatMessages.some((message) => {
            if (message.isDeleted) return false;
            if (getId(message.sender) === getId(userId)) return false;
            const readBy = Array.isArray(message.readBy) ? message.readBy.map(getId) : [];
            return !readBy.includes(getId(userId));
        });

        if (hasUnread) scheduleMarkRead();
    }, [chatId, userId, chatMessages, scheduleMarkRead]);

    useEffect(() => {
        if (!chatId || !userId || typeof window === "undefined" || typeof document === "undefined") {
            return undefined;
        }

        const handleEngagementChange = () => {
            if (!isPageEngaged()) {
                if (markReadTimerRef.current) {
                    clearTimeout(markReadTimerRef.current);
                    markReadTimerRef.current = null;
                }
                return;
            }

            scheduleMarkRead();
        };

        document.addEventListener("visibilitychange", handleEngagementChange);
        window.addEventListener("focus", handleEngagementChange);
        window.addEventListener("blur", handleEngagementChange);

        return () => {
            document.removeEventListener("visibilitychange", handleEngagementChange);
            window.removeEventListener("focus", handleEngagementChange);
            window.removeEventListener("blur", handleEngagementChange);
        };
    }, [chatId, userId, scheduleMarkRead]);

    useEffect(() => {
        if (!chatId || !userId) return undefined;

        const handleNewMessage = (message) => {
            if (getId(message.chatId) !== getId(chatId)) return;

            const mine = getId(message.sender) === getId(userId);
            const shouldStick = atBottomRef.current || mine;

            if (!realtimeManagedRef.current) {
                dispatch(receiveMessage(message, userId));
            }

            if (!mine) {
                scheduleMarkRead();
                if (document.hidden && !realtimeManagedRef.current) playDing();
            }

            if (shouldStick) {
                requestScroll();

                // ChatRealtimeBridge can dispatch the same socket event before
                // this local listener runs. A frame-level fallback guarantees
                // that a user who was already at the bottom stays attached to
                // the newest rendered message.
                requestAnimationFrame(() => {
                    if (atBottomRef.current || mine) {
                        scrollToBottom();
                    }
                });
            } else if (!mine) {
                setShowJumpToBottom(true);
                setNewMessagesCount((count) => count + 1);
            }
        };

        const handleUpdated = (payload) => {
            if (realtimeManagedRef.current) return;
            const message = payload?.message || payload;
            if (getId(message?.chatId) === getId(chatId) && message?._id) {
                dispatch(receiveMessageUpdate(message));
            }
        };

        const handleDeleted = (payload) => {
            const messageId = payload?.messageId || payload;
            if (messageId) dispatch(deleteMessageSocket(messageId));
        };

        const handleRead = ({ chatId: readChatId, userId: readUserId }) => {
            if (getId(readChatId) !== getId(chatId)) return;
            dispatch(markMessagesAsReadSocket(chatId, readUserId, userId));
        };

        const handleTypingStart = ({ chatId: typingChatId, user: typingUser }) => {
            if (getId(typingChatId) !== getId(chatId)) return;
            if (!typingUser?._id || getId(typingUser._id) === getId(userId)) return;

            setTypingUsers((prev) => (
                prev.some((item) => getId(item._id) === getId(typingUser._id))
                    ? prev
                    : [...prev, typingUser]
            ));
        };

        const handleTypingStop = ({ chatId: typingChatId, userId: typingUserId }) => {
            if (getId(typingChatId) !== getId(chatId)) return;
            setTypingUsers((prev) => prev.filter((item) => getId(item._id) !== getId(typingUserId)));
        };

        joinSocketUser();

        // Каждый ChatCore получает lease на room через общий owner-manager.
        // Если тот же chatId уже держит office bridge или другое окно ChatCore,
        // физический join выполняется один раз, а leave уйдёт только после
        // освобождения последнего владельца.
        acquireChatRoom(chatId, roomOwnerRef.current);

        socket.on("newMessage", handleNewMessage);
        socket.on("messageDeleted", handleDeleted);
        socket.on("messageUpdated", handleUpdated);
        socket.on("messagesRead", handleRead);
        socket.on("typing:start", handleTypingStart);
        socket.on("typing:stop", handleTypingStop);

        return () => {
            releaseChatRoom(chatId, roomOwnerRef.current);
            socket.off("newMessage", handleNewMessage);
            socket.off("messageDeleted", handleDeleted);
            socket.off("messageUpdated", handleUpdated);
            socket.off("messagesRead", handleRead);
            socket.off("typing:start", handleTypingStart);
            socket.off("typing:stop", handleTypingStop);
        };
    }, [dispatch, chatId, userId, requestScroll, scheduleMarkRead, scrollToBottom]);

    const handleScroll = () => {
        const el = scrollRef.current;
        if (!el) return;

        const atBottom = el.scrollHeight - (el.scrollTop + el.clientHeight) < 90;
        atBottomRef.current = atBottom;
        syncViewportState(atBottom);
        setShowJumpToBottom(!atBottom);

        if (atBottom) {
            setNewMessagesCount(0);
            scheduleMarkRead();
        }
        if (el.scrollTop <= 90) loadOlderMessages();
    };

    const emitTyping = () => {
        if (!chatId || !userId) return;

        joinSocketUser();
        socket.emit("typing:start", {
            chatId,
            user: {
                _id: userId,
                firstName: user?.firstName,
                lastName: user?.lastName,
                email: user?.email,
            },
        });

        if (typingTimerRef.current) clearTimeout(typingTimerRef.current);
        typingTimerRef.current = setTimeout(() => {
            socket.emit("typing:stop", { chatId, userId });
        }, 1200);
    };

    const showNotice = useCallback((value) => {
        setNotice(value);
        if (noticeTimerRef.current) window.clearTimeout(noticeTimerRef.current);
        noticeTimerRef.current = window.setTimeout(() => setNotice(""), 3200);
    }, []);

    useEffect(() => () => {
        if (noticeTimerRef.current) window.clearTimeout(noticeTimerRef.current);
        if (highlightTimerRef.current) window.clearTimeout(highlightTimerRef.current);
    }, []);

    /** Добавить файлы к сообщению: не больше 10 и не больше 50 МБ каждый. */
    const addFiles = useCallback((list) => {
        const incoming = Array.from(list || []).filter(Boolean);
        if (!incoming.length) return;

        const tooBig = incoming.filter((item) => Number(item.size || 0) > MAX_FILE_BYTES);
        const fitting = incoming.filter((item) => Number(item.size || 0) <= MAX_FILE_BYTES);

        setFiles((current) => {
            const room = Math.max(0, MAX_ATTACHMENTS - current.length);
            const next = [...current, ...fitting.slice(0, room)];
            const messages = [];
            if (tooBig.length) messages.push(`Слишком большие (больше 50 МБ): ${tooBig.map((item) => item.name).join(", ")}`);
            if (fitting.length > room) messages.push(`За раз можно отправить до ${MAX_ATTACHMENTS} файлов — лишние не добавлены.`);
            setAttachError(messages.join(" "));
            return next;
        });
        setTimeout(() => inputRef.current?.focus?.({ preventScroll: true }), 0);
    }, []);

    const removeFile = useCallback((index) => {
        setFiles((current) => current.filter((_, itemIndex) => itemIndex !== index));
        setAttachError("");
    }, []);

    const clearFiles = useCallback(() => {
        setFiles([]);
        setAttachError("");
    }, []);

    const replyPayloadOf = (value) => (value ? {
        messageId: value.messageId,
        author: value.author,
        snippet: value.snippet,
    } : null);

    // ---------- отложенные сообщения ----------

    const reloadScheduled = useCallback(async () => {
        if (!chatId || mode === "floating") return;
        try {
            setScheduled(await fetchScheduledMessages(chatId));
        } catch (_error) {
            // Нет связи или сервер старый — плашку просто не показываем.
        }
    }, [chatId, mode]);

    useEffect(() => {
        reloadScheduled();
    }, [reloadScheduled]);

    // Сервер сообщил, что список изменился (ушло, удалили с телефона).
    useEffect(() => {
        if (!chatId) return undefined;
        const handleChanged = (payload) => {
            if (payload?.chatId && getId(payload.chatId) !== getId(chatId)) return;
            reloadScheduled();
        };
        socket.on("scheduled:changed", handleChanged);
        return () => socket.off("scheduled:changed", handleChanged);
    }, [chatId, reloadScheduled]);

    // Подошло время ближайшего — перечитываем: сообщение уже ушло в чат.
    useEffect(() => {
        const times = scheduled
            .filter((item) => item.status === "pending")
            .map((item) => new Date(item.sendAt).getTime())
            .filter((time) => Number.isFinite(time));
        if (!times.length) return undefined;

        const wait = Math.min(Math.max(Math.min(...times) - Date.now() + 8000, 8000), 6 * 60 * 60 * 1000);
        const timer = window.setTimeout(reloadScheduled, wait);
        return () => window.clearTimeout(timer);
    }, [reloadScheduled, scheduled]);

    /** Кладём в отложенные: по одному, файлы грузятся сразу. */
    const scheduleDrafts = useCallback(async (drafts) => {
        if (!chatId || !drafts.length) return;
        setScheduling((count) => count + drafts.length);
        if (drafts.some((draft) => draft.file)) showNotice("Загружаем — не закрывайте страницу…");

        let done = 0;
        let failText = "";
        for (const draft of drafts) {
            try {
                await createScheduledMessage(chatId, draft);
                done += 1;
            } catch (error) {
                failText = scheduledErrorText(error, "Не удалось отложить сообщение. Проверьте связь.");
            } finally {
                setScheduling((count) => Math.max(0, count - 1));
            }
        }

        reloadScheduled();
        if (failText) {
            showNotice(done ? `Отложено ${done} из ${drafts.length}. ${failText}` : failText);
            return;
        }
        showNotice(`Отправится: ${scheduleText(drafts[0].sendAt).toLowerCase()}`);
    }, [chatId, reloadScheduled, showNotice]);

    const handleSend = async ({ text: messageText, files: messageFiles = [], sendAt = null }) => {
        if (!chatId) return false;

        const cleanText = (messageText || "").trim();
        const list = Array.from(messageFiles || []);
        if (!cleanText && !list.length) return false;

        const replyPayload = replyPayloadOf(replyTo);

        /*
         * Несколько фото разом — альбом: в чате они встанут одним сообщением с
         * сеткой. Фото идут первыми (подпись и ответ — у первого), видео и
         * файлы — следом отдельными сообщениями.
         */
        const photos = list.filter(isAlbumPhoto);
        const albumId = photos.length > 1 ? createAlbumId() : "";
        const ordered = albumId ? [...photos, ...list.filter((item) => !isAlbumPhoto(item))] : list;

        const resetComposer = () => {
            setText("");
            setFiles([]);
            setAttachError("");
            setReplyTo(null);
            setSelectedId(null);
            setTimeout(() => inputRef.current?.focus?.({ preventScroll: true }), 0);
        };

        // «Отправить позже»: всё уходит на сервер и ждёт там своего времени.
        if (sendAt) {
            const drafts = ordered.length
                ? ordered.map((item, index) => ({
                    sendAt,
                    text: index === 0 ? cleanText : "",
                    replyTo: index === 0 ? replyPayload : null,
                    file: item,
                    ...(albumId && isAlbumPhoto(item) ? { albumId } : {}),
                }))
                : [{ sendAt, text: cleanText, replyTo: replyPayload }];
            resetComposer();
            scheduleDrafts(drafts);
            return true;
        }

        // Файлы: каждый сразу появляется в чате и грузится сам по себе —
        // писать дальше можно не дожидаясь.
        if (ordered.length) {
            resetComposer();
            requestScroll();
            ordered.forEach((item, index) => {
                dispatch(sendMediaMessage(chatId, {
                    sender: userId,
                    senderUser: user,
                    text: index === 0 ? cleanText : "",
                    replyTo: index === 0 ? replyPayload : null,
                    file: item,
                    ...(albumId && isAlbumPhoto(item)
                        ? { albumId, clientMessageId: albumClientId(albumId, index) }
                        : {}),
                }));
            });
            requestAnimationFrame(() => scrollToBottom());
            return true;
        }

        // Быстрые короткие сообщения отправляем optimistic:
        // UI очищается ДО завершения REST и следующий Enter доступен сразу.
        if (cleanText) {
            setText("");
            setReplyTo(null);
            setSelectedId(null);
            requestScroll();
            setTimeout(() => inputRef.current?.focus?.({ preventScroll: true }), 0);

            dispatch(sendTextMessage(chatId, {
                sender: userId,
                senderUser: user,
                text: cleanText,
                replyTo: replyPayload,
            }));

            return true;
        }

        return false;
    };

    const handleShareEntity = async (item) => {
        if (!chatId || !item?.type || !item?.id || sending) return false;
        let sent = null;
        if (CHAT_SITE_CATALOG && item.type === 'site_product') sent = await dispatch(shareProductToChat(chatId, item.id));
        else if (CHAT_SITE_CATALOG && item.type === 'site_order') sent = await dispatch(shareOrderToChat(chatId, item.id));
        else if (officeId) sent = await dispatch(shareEntityToChat(chatId, {
            type: item.type,
            entityId: item.id,
            officeId,
        }));

        if (!sent) return false;

        requestScroll();
        requestAnimationFrame(() => scrollToBottom());
        setTimeout(() => inputRef.current?.focus?.({ preventScroll: true }), 0);
        return true;
    };

    /** Запись (голосовое или кружок) — файлом с понятным именем и типом. */
    const recordingFile = (blob, prefix) => {
        const mimeType = String(blob?.type || "").split(";")[0] || (prefix === "voice" ? "audio/webm" : "video/webm");
        return new File([blob], `${prefix}-${Date.now()}.${recordingExtension(mimeType)}`, { type: mimeType });
    };

    const handleVoiceFinished = async ({ blob, durationMs, sendAt = null }) => {
        if (!blob || !chatId) return false;

        const replyPayload = replyPayloadOf(replyTo);
        setReplyTo(null);
        setSelectedId(null);

        if (sendAt) {
            scheduleDrafts([{
                sendAt,
                replyTo: replyPayload,
                mediaDurationSec: Math.max(1, Math.round((Number(durationMs) || 0) / 1000)),
                file: recordingFile(blob, "voice"),
            }]);
            return true;
        }

        requestScroll();
        dispatch(sendVoiceMessage(
            chatId,
            { sender: userId, senderUser: user, durationMs, replyTo: replyPayload },
            blob
        ));
        return true;
    };

    /** Кружок записан: отправляем сразу или откладываем. */
    const handleVideoNote = ({ blob, durationMs }, sendAt = null) => {
        if (!blob || !chatId) return;

        const replyPayload = replyPayloadOf(replyTo);
        setVideoNoteOpen(false);
        setNoteToSchedule(null);
        setReplyTo(null);
        setSelectedId(null);

        if (sendAt) {
            scheduleDrafts([{
                sendAt,
                replyTo: replyPayload,
                messageKind: "video_note",
                mediaDurationSec: Math.max(1, Math.round((Number(durationMs) || 0) / 1000)),
                file: recordingFile(blob, "note"),
            }]);
            return;
        }

        requestScroll();
        dispatch(sendVideoNoteMessage(
            chatId,
            { sender: userId, senderUser: user, durationMs, replyTo: replyPayload },
            blob
        ));
        requestAnimationFrame(() => scrollToBottom());
    };

    // ---------- переход к сообщению, повтор, удаление ----------

    /** Нажали на цитату: показываем сообщение, на которое ответили (подгружаем историю, если оно старое). */
    const jumpToMessage = useCallback(async (messageId) => {
        const id = getId(messageId);
        if (!id || !chatId) return;

        const highlight = () => {
            setHighlightId(id);
            if (highlightTimerRef.current) window.clearTimeout(highlightTimerRef.current);
            highlightTimerRef.current = window.setTimeout(() => setHighlightId(""), 2200);
        };

        if (scrollToMessage(id)) {
            highlight();
            return;
        }

        showNotice("Ищем сообщение в истории…");
        let cursor = nextCursor;
        for (let page = 0; cursor && page < 30; page += 1) {
            const older = await dispatch(getMessagesByChat(chatId, { before: cursor, limit: 100, append: true }));
            if ((older?.messages || []).some((item) => getId(item?._id) === id)) {
                await new Promise((resolve) => window.requestAnimationFrame(() => window.requestAnimationFrame(resolve)));
                if (scrollToMessage(id)) {
                    highlight();
                    setNotice("");
                    return;
                }
            }
            cursor = older?.nextCursor || null;
        }
        showNotice("Сообщение не найдено — возможно, оно удалено");
    }, [chatId, dispatch, nextCursor, scrollToMessage, showNotice]);

    const handleRetry = useCallback((message) => {
        const clientMessageId = String(message?.__clientMessageId || "");
        if (!clientMessageId) return;
        if (!canRetryMessage(clientMessageId)) {
            showNotice("Файла уже нет в памяти страницы — выберите его и отправьте заново");
            return;
        }
        dispatch(retryFailedMessage(clientMessageId));
    }, [dispatch, showNotice]);

    const handleDiscard = useCallback((message) => {
        const clientMessageId = String(message?.__clientMessageId || "");
        if (clientMessageId) dispatch(discardFailedMessage(clientMessageId));
    }, [dispatch]);

    const runDelete = async (scope) => {
        const targets = deleteTargets || [];
        if (!targets.length) return;

        let failed = 0;
        for (const message of targets) {
            const ok = await dispatch(deleteMessage(message._id, chatId, "user", scope));
            if (!ok) failed += 1;
        }

        setDeleteTargets(null);
        setSelectedMessageIds([]);
        setSelectionLimitHit(false);
        if (failed) showNotice(failed === targets.length ? "Не удалось удалить" : `Не удалось удалить: ${failed}`);
    };

    const openEditMessage = (message) => {
        if (!message?._id || String(message._id).startsWith('local:')) return;
        setEditingMessage(message);
        const original = String(message.text || '');
        const splitAt = original.startsWith('↩️ ') ? original.indexOf('\n') : -1;
        setEditText(splitAt >= 0 ? original.slice(splitAt + 1) : original);
        setEditError('');
    };

    const saveEditedMessage = async (event) => {
        event?.preventDefault?.();
        if (!editingMessage || editBusy) return;
        const original = String(editingMessage.text || '');
        const splitAt = original.startsWith('↩️ ') ? original.indexOf('\n') : -1;
        const next = splitAt >= 0
            ? `${original.slice(0, splitAt)}\n${editText.trim()}`
            : editText.trim();
        if (!next.trim() && !editingMessage.fileName && !editingMessage.secureAttachment) {
            setEditError('Сообщение не может быть пустым');
            return;
        }
        setEditBusy(true);
        setEditError('');
        try {
            const updated = await dispatch(editMessageText(editingMessage._id, next));
            if (!updated) throw new Error('Сервер не подтвердил изменение');
            setEditingMessage(null);
        } catch (error) {
            setEditError(error?.response?.data?.error || error?.message || 'Не удалось изменить сообщение');
        } finally {
            setEditBusy(false);
        }
    };

    const clearMessageSelection = useCallback(() => {
        setSelectedMessageIds([]);
        setSelectionLimitHit(false);
    }, []);

    const addMessageSelection = useCallback((message) => {
        const messageId = getId(message?._id);
        if (!messageId || messageId.startsWith("local:") || message?.isDeleted) return;

        setReplyTo(null);
        setSelectedId(null);
        setSelectedMessageIds((prev) => {
            if (prev.some((id) => getId(id) === messageId)) return prev;
            if (prev.length >= 100) {
                setSelectionLimitHit(true);
                return prev;
            }

            setSelectionLimitHit(false);
            return [...prev, messageId];
        });
    }, []);

    const toggleMessageSelection = useCallback((message) => {
        // Альбом отмечается целиком: все его фото разом (или разом снимаются).
        if (Array.isArray(message)) {
            const ids = message
                .filter((item) => item?._id && !String(item._id).startsWith("local:") && !item?.isDeleted)
                .map((item) => getId(item._id));
            if (!ids.length) return;

            setReplyTo(null);
            setSelectedId(null);
            setSelectedMessageIds((prev) => {
                const current = new Set(prev.map(getId));
                if (ids.every((id) => current.has(id))) {
                    setSelectionLimitHit(false);
                    return prev.filter((id) => !ids.includes(getId(id)));
                }
                const next = [...prev];
                for (const id of ids) {
                    if (current.has(id)) continue;
                    if (next.length >= 100) {
                        setSelectionLimitHit(true);
                        break;
                    }
                    next.push(id);
                }
                return next;
            });
            return;
        }

        const messageId = getId(message?._id);
        if (!messageId || messageId.startsWith("local:") || message?.isDeleted) return;

        setReplyTo(null);
        setSelectedId(null);
        setSelectedMessageIds((prev) => {
            const exists = prev.some((id) => getId(id) === messageId);
            if (exists) {
                setSelectionLimitHit(false);
                return prev.filter((id) => getId(id) !== messageId);
            }

            if (prev.length >= 100) {
                setSelectionLimitHit(true);
                return prev;
            }

            setSelectionLimitHit(false);
            return [...prev, messageId];
        });
    }, []);

    const requestForward = useCallback((items) => {
        const normalized = (Array.isArray(items) ? items : [items])
            .filter((message) => message?._id && !String(message._id).startsWith("local:"))
            .slice(0, 100);

        if (!normalized.length) return;

        onForwardRequest?.(normalized, {
            onComplete: clearMessageSelection,
        });
    }, [clearMessageSelection, onForwardRequest]);

    const selectionAllMine = selectedMessages.length > 0 &&
        selectedMessages.every((message) => getId(message.sender) === getId(userId));
    const selectionFiles = selectedMessages.filter((message) => hasSecureChatAttachment(message));

    const copySelected = async () => {
        const texts = selectedMessages.map(getCopyText).filter(Boolean);
        if (!texts.length) {
            showNotice("В выбранных сообщениях нет текста");
            return;
        }
        const ok = await copyToClipboard(texts.join("\n\n"));
        showNotice(ok ? (texts.length > 1 ? `Скопировано сообщений: ${texts.length}` : "Текст скопирован") : "Не удалось скопировать");
    };

    /** Скачать все файлы из выбранных сообщений — по одному. */
    const downloadSelected = async () => {
        if (!selectionFiles.length) return;
        showNotice(selectionFiles.length > 1 ? `Скачиваем файлов: ${selectionFiles.length}` : "Скачиваем файл…");
        let failed = 0;
        for (const message of selectionFiles) {
            try {
                const asset = await fetchChatAttachment({ message, download: true, useCache: false });
                downloadBlob({ blob: asset.blob, filename: asset.filename || getChatAttachmentName(message) });
                // Браузер не любит много скачиваний разом — даём ему передышку.
                await new Promise((resolve) => window.setTimeout(resolve, 350));
            } catch (_error) {
                failed += 1;
            }
        }
        if (failed) showNotice(`Не удалось скачать: ${failed}`);
    };

    const selectionToTask = TodoFromMessageModal ? () => {
        const list = selectedMessages.filter((message) => message?.type !== "call" && !message?.call);
        if (!list.length) return;
        if (list.length > MAX_TODO_MESSAGES) showNotice(`За один раз — не больше ${MAX_TODO_MESSAGES} сообщений, взяты первые`);
        setTodoMessages(list.slice(0, MAX_TODO_MESSAGES));
    } : null;

    // ---------- перетаскивание файлов в чат ----------

    const hasDraggedFiles = (event) => Array.from(event.dataTransfer?.types || []).includes("Files");

    const dropHandlers = mode === "floating" ? {} : {
        onDragEnter: (event) => {
            if (!hasDraggedFiles(event)) return;
            event.preventDefault();
            dragDepthRef.current += 1;
            setDragOver(true);
        },
        onDragOver: (event) => {
            if (!hasDraggedFiles(event)) return;
            event.preventDefault();
            event.dataTransfer.dropEffect = "copy";
        },
        onDragLeave: (event) => {
            if (!hasDraggedFiles(event)) return;
            dragDepthRef.current = Math.max(0, dragDepthRef.current - 1);
            if (!dragDepthRef.current) setDragOver(false);
        },
        onDrop: (event) => {
            if (!hasDraggedFiles(event)) return;
            event.preventDefault();
            dragDepthRef.current = 0;
            setDragOver(false);
            addFiles(event.dataTransfer.files);
        },
    };

    const handleReply = (message) => {
        const payload = getReplyPayload(message);
        setReplyTo(payload);
        setSelectedId(message?._id || null);
        setTimeout(() => inputRef.current?.focus?.({ preventScroll: true }), 0);
    };

    /** Открыть этот чат в плавающем окне (есть не во всех проектах). */
    const handleOpenFloating = openChatFloating && mode !== "floating" ? () => {
        if (!chat?._id) return;
        openChatFloating({
            chat,
            officeId: officeId || chat?.officeId?._id || chat?.officeId || null,
            office: office || null,
            role: role || null,
            title: chat?.title || chat?.entity?.title || undefined,
            source: chat?.entity?.type && chat.entity.type !== "none"
                ? {
                    type: chat.entity.type,
                    id: chat.entity.id || chat.entity.entityId || null,
                    label: chat.entity.title || chat.title || "Обсуждение",
                }
                : officeId
                    ? { type: "office", id: officeId, label: office?.name || "Офисный чат" }
                    : { type: "personal", label: "Личный чат" },
        });
    } : undefined;

    const chatBackgroundClass = `chat-core-AO--bg-${chatBackground?.preset || "default"}`;
    const chatBackgroundStyle = chatBackground?.preset === "custom" && chatBackground?.image
        ? { "--chat-wallpaper-image": `url(${chatBackground.image})` }
        : undefined;

    return (
        <div className={`chat-core-AO ${chatBackgroundClass} ${dragOver ? "is-drag-over" : ""}`} style={chatBackgroundStyle} {...dropHandlers}>
            {dragOver && (
                <div className="chat-core-AO__dropzone" aria-hidden="true">
                    <b>Отпустите, чтобы прикрепить</b>
                    <small>До {MAX_ATTACHMENTS} файлов за раз · фото уйдут альбомом</small>
                </div>
            )}

            {onBack && (
                <button type="button" className="chat-core-AO__mobile-back" onClick={onBack}>
                    ← К списку чатов
                </button>
            )}

            {mode !== "floating" && (
                <HeaderChatUI
                    chat={chat}
                    userId={userId}
                    officeId={officeId}
                    office={office}
                    role={role}
                    typingUsers={typingUsers}
                    backgroundPreset={chatBackground?.preset || "default"}
                    onBackgroundChange={handleChatBackgroundChange}
                    onOpenFloating={handleOpenFloating}
                    onOpenProfile={ChatUserProfileModal ? openProfile : null}
                    presenceFor={presenceFor}
                />
            )}


            <div className="chat-core-AO__messages" ref={scrollRef} onScroll={handleScroll}>
                {loadingOlder && <div className="chat-core-AO__older-loader">Загружаем историю…</div>}

                {loading && !chatMessages.length ? (
                    <div className="chat-core-AO__loader">
                        <span className="chat-core-AO__spinner" />
                        Загрузка сообщений…
                    </div>
                ) : (
                    <ChatMessages
                        messages={chatMessages}
                        currentUserId={userId}
                        selectedId={selectedId}
                        selectedMessageIds={selectedMessageIds}
                        selectionMode={selectedMessageIds.length > 0}
                        chats={chats}
                        focusMessageId={highlightId || focusMessageId}
                        onReply={handleReply}
                        onEdit={openEditMessage}
                        onDelete={(id) => dispatch(deleteMessage(id, chatId, "user"))}
                        onDeleteRequest={(list) => setDeleteTargets(list)}
                        onForward={onForwardRequest ? (message) => requestForward([message]) : null}
                        onOpenForwardOrigin={onOpenForwardOrigin}
                        onSelectMessage={toggleMessageSelection}
                        onAddMessageSelection={addMessageSelection}
                        onJumpToMessage={jumpToMessage}
                        onRetry={handleRetry}
                        onDiscard={handleDiscard}
                        onOpenProfile={ChatUserProfileModal ? openProfile : null}
                        officeId={officeId}
                    />
                )}

            </div>

            {showJumpToBottom && (
                <button
                    type="button"
                    className="chat-core-AO__jump-bottom"
                    onClick={() => scrollToBottom("smooth")}
                    title="К последним сообщениям"
                >
                    ↓
                    {newMessagesCount > 0 && <b>{newMessagesCount > 99 ? "99+" : newMessagesCount}</b>}
                </button>
            )}

            {selectedMessages.length > 0 ? (
                <div className="chat-core-AO__selection-bar">
                    <button
                        type="button"
                        className="chat-core-AO__selection-close"
                        onClick={clearMessageSelection}
                        title="Отменить выбор"
                        aria-label="Отменить выбор"
                    >
                        ✕
                    </button>

                    <div className="chat-core-AO__selection-count">
                        <b>Выбрано: {selectedMessages.length}</b>
                        <span>/ 100</span>
                        {selectionLimitHit && <small>Лимит — 100 сообщений</small>}
                    </div>

                    <div className="chat-core-AO__selection-actions">
                        <button type="button" onClick={copySelected} title="Скопировать текст выбранных сообщений">
                            ⧉ <span>Копировать</span>
                        </button>
                        {selectionToTask && (
                            <button type="button" onClick={selectionToTask} title="Сделать задачу из выбранных сообщений или добавить их в задачу">
                                ✓ <span>В задачу</span>
                            </button>
                        )}
                        {selectionFiles.length > 0 && (
                            <button type="button" onClick={downloadSelected} title="Скачать файлы из выбранных сообщений">
                                ↓ <span>Скачать ({selectionFiles.length})</span>
                            </button>
                        )}
                        {selectionAllMine && (
                            <button type="button" className="is-danger" onClick={() => setDeleteTargets(selectedMessages)} title="Удалить выбранные">
                                🗑 <span>Удалить</span>
                            </button>
                        )}
                        {onForwardRequest && (
                            <button
                                type="button"
                                className="chat-core-AO__selection-forward"
                                onClick={() => requestForward(selectedMessages)}
                            >
                                ↗ Переслать
                            </button>
                        )}
                    </div>
                </div>
            ) : (
                <>
                    {replyTo && (
                        <div className="chat-core-AO__reply">
                            <div className="chat-core-AO__reply-main">
                                <div className="chat-core-AO__reply-title">Ответ</div>
                                <div className="chat-core-AO__reply-author">{replyTo.author}</div>
                                <div className="chat-core-AO__reply-text">{replyTo.snippet}</div>
                            </div>
                            <button
                                type="button"
                                className="chat-core-AO__reply-close"
                                onClick={() => {
                                    setReplyTo(null);
                                    setSelectedId(null);
                                }}
                                aria-label="Отменить ответ"
                            >
                                ✕
                            </button>
                        </div>
                    )}

                    {/* Отложенные сообщения этого чата: нажали — открылся список. */}
                    {(scheduled.length > 0 || scheduling > 0) && (
                        <button
                            type="button"
                            className={`chat-core-AO__scheduled-chip ${scheduled.some((item) => item.status === "failed") ? "is-failed" : ""}`}
                            onClick={() => scheduled.length && setScheduledOpen(true)}
                        >
                            {scheduling > 0 ? <i className="chat-album-AO__spinner" /> : <span>◷</span>}
                            {scheduling > 0 ? "Откладываем…" : `Отложенные: ${scheduled.length}`}
                        </button>
                    )}

                    <InputChatUI
                        text={text}
                        setText={(value) => {
                            setText(value);
                            emitTyping();
                        }}
                        files={files}
                        onAddFiles={addFiles}
                        onRemoveFile={removeFile}
                        onClearFiles={clearFiles}
                        inputRef={inputRef}
                        officeId={officeId}
                        onSend={handleSend}
                        // Товары и заказы сайта — в любой чат; объекты офиса — только в чат офиса.
                        onShareEntity={CHAT_SITE_CATALOG || officeId ? handleShareEntity : null}
                        onVoiceFinished={handleVoiceFinished}
                        onOpenVideoNote={() => setVideoNoteOpen(true)}
                        scheduleEnabled={mode !== "floating"}
                        sending={sending}
                        sendError={sendError}
                        attachError={attachError}
                    />
                </>
            )}
            {notice && (
                <div className="chat-core-AO__notice" role="status">{notice}</div>
            )}

            {videoNoteOpen && (
                <VideoNoteRecorder
                    onClose={() => setVideoNoteOpen(false)}
                    onSend={(result) => handleVideoNote(result)}
                    onSchedule={mode !== "floating" ? (result) => setNoteToSchedule(result) : null}
                />
            )}

            {noteToSchedule && (
                <ScheduleDialog
                    title="Отправить кружок позже"
                    onClose={() => setNoteToSchedule(null)}
                    onConfirm={(sendAt) => handleVideoNote(noteToSchedule, sendAt)}
                />
            )}

            {scheduledOpen && (
                <ScheduledMessagesDialog
                    items={scheduled}
                    onClose={() => setScheduledOpen(false)}
                    onChanged={reloadScheduled}
                    onNotify={showNotice}
                />
            )}

            {deleteTargets && (
                <DeleteMessagesDialog
                    messages={deleteTargets}
                    meId={userId}
                    onClose={() => setDeleteTargets(null)}
                    onConfirm={runDelete}
                />
            )}

            {profileUser && ChatUserProfileModal && (
                <ChatUserProfileModal
                    open
                    userId={getId(profileUser)}
                    user={typeof profileUser === "object" ? profileUser : null}
                    chatId={chatId}
                    officeId={officeId}
                    presence={presenceFor ? presenceFor(getId(profileUser)) : null}
                    onClose={closeProfile}
                />
            )}

            {todoMessages && TodoFromMessageModal && (
                <TodoFromMessageModal
                    messages={todoMessages}
                    onClose={() => {
                        setTodoMessages(null);
                        setSelectedMessageIds([]);
                    }}
                />
            )}

            {editingMessage && (
                <div className="chat-edit-message-AO" role="presentation" onMouseDown={(event) => {
                    if (event.target === event.currentTarget && !editBusy) setEditingMessage(null);
                }}>
                    <form className="chat-edit-message-AO__panel" onSubmit={saveEditedMessage} role="dialog" aria-modal="true" aria-label="Редактировать сообщение">
                        <strong>Редактировать сообщение</strong>
                        <textarea
                            autoFocus
                            value={editText}
                            onChange={(event) => setEditText(event.target.value)}
                            maxLength={20000}
                            rows={5}
                            disabled={editBusy}
                            onKeyDown={(event) => {
                                if (event.key === 'Escape' && !editBusy) setEditingMessage(null);
                                if (event.key === 'Enter' && (event.ctrlKey || event.metaKey)) {
                                    event.preventDefault();
                                    saveEditedMessage(event);
                                }
                            }}
                        />
                        {editError && <span role="alert" className="chat-edit-message-AO__error">{editError}</span>}
                        <div className="chat-edit-message-AO__actions">
                            <button type="button" disabled={editBusy} onClick={() => setEditingMessage(null)}>Отмена</button>
                            <button type="submit" disabled={editBusy || editText.length > 20000}>{editBusy ? 'Сохраняем…' : 'Сохранить'}</button>
                        </div>
                    </form>
                </div>
            )}
        </div>
    );
}
